import { useEffect, useMemo, useState } from "react";
import { CONCEPT_CATEGORIES } from "@/data/conceptCategories";
import { API } from "@/lib/api";

// Flat {slug, href} list built once, sorted longest-href-first so a sub-page like
// "/trenas-law-iloilo-concept/contact" resolves to the same slug as its parent
// "/trenas-law-iloilo-concept" without every multi-page firm needing its own entry.
// Shared by ConceptLifecycleGate (disclaimer/CTA visibility) and BusinessSeo (robots
// meta) so both agree on which real slug a given concept path belongs to.
const ROUTE_INDEX = CONCEPT_CATEGORIES
  .flatMap((cat) => cat.items)
  .map((it) => ({ slug: it.slug, href: it.href }))
  .sort((a, b) => b.href.length - a.href.length);

export function slugForConceptPath(pathname) {
  const match = ROUTE_INDEX.find((r) => pathname === r.href || pathname.startsWith(`${r.href}/`));
  return match?.slug || null;
}

// Resolves the confirmed lifecycle status ("offline_concept" | "proposal_accepted" |
// "online_client" | ...) for the concept route matching `pathname`, via the one
// authoritative backend source (GET /api/sites/:slug/status).
// Fail-safe: starts at null on every path change and only a successful response can
// set it. A fetch failure, non-concept path, or in-flight request all resolve to null
// — never a false "online_client" — because null is the safe default for both the
// disclaimer/CTA visibility gate and the robots noindex logic that consume this.
export function useConceptLifecycleStatus(pathname) {
  const slug = useMemo(() => slugForConceptPath(pathname), [pathname]);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    setStatus(null);
    if (!slug || !API) return undefined;

    let cancelled = false;
    fetch(`${API}/sites/${slug}/status`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data) setStatus(data.status || null);
      })
      .catch(() => {
        // Network/backend failure: leave status at null, the safe default.
      });

    return () => { cancelled = true; };
  }, [slug]);

  return { slug, status };
}
